import { Box, Button, Divider, HStack, Spinner, Stack, Text, VStack } from "@chakra-ui/react";
import Template from "../../../../components/Layout/Template";
import { colors } from "../../../../styles";
import { useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyLikes, getMyProfile } from "../../../../api";
import { IChatList, ILikedChatList, IMyProfile } from "../../../../types";
import { Fragment, useEffect, useMemo } from "react";
import MyIcon from "../../../../components/Icon/MyIcon";
import LikesChatListItem from "../../../../components/Chat/ListItem/LikesChatListItem";
import { useAuthStore } from "../../../../store";
import { ChatDeleteLoadingProvider } from "../../../../components/Chat/ChatDeleteLoading";

export default function Likes() {

    const { isGuest } = useAuthStore();
    const queryClient = useQueryClient();

    /* query */

    const { data: myProfileData } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: () => getMyProfile(),
    });

    const nickname = myProfileData?.nickname;

    /* navigate */

    const navigate = useNavigate();

    /* useInfinitequery */

    const size = 20;

    const { 
        data: likedChatListData, 
        isLoading: isLikedChatListLoading, 
        isFetching: isLikedChatListFetching,
        fetchNextPage: fetchNextLikedChatList, 
        hasNextPage: hasNextLikedChatListPage, 
        isFetchingNextPage: isFetchingNextLikedChatListPage, 
    } = useInfiniteQuery<ILikedChatList>({

        // 쿼리 키 (단어 검색 전용, 필터 포함)
        queryKey: ['getMyLikes'],

        // 쿼리 함수 - pageParam은 useInfiniteQuery의 파라미터로 전달되는 값 중 페이지 번호를 의미
        queryFn: ({ pageParam }) => {

            // pageParam은 unknown 타입이므로 명시적으로 number 타입으로 변환
            const page = pageParam as number;
            
            return getMyLikes({ 
                page, 
                size, 
            });
        },

        // 초기 페이지 파라미터 : 1 또는 0 사용
        initialPageParam: 1,

        // 쿼리 활성화 조건: 검색어가 확정된 경우에만
        enabled: !isGuest,

        // 다음 페이지 파라미터 결정
        getNextPageParam: (lastPageData: ILikedChatList) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        },
    });

    /* renderLikedChatList */

    const renderLikedChatList = () => {

        if (isLikedChatListLoading) {
            return (
                <VStack width={"100%"} justifyContent={"center"} alignItems={"center"} paddingY={10}>
                    <Spinner color={colors.blue} />
                </VStack>
            );
        }

        return (
            <VStack 
                width={"100%"} 
                justifyContent={"flex-start"} 
                alignItems={"center"}
                spacing={5}

                marginTop={10}
            >
                {likedChatListData?.pages.map((page) => (
                    page.likedChats.map((chat, index) => {
                        const isFirst = index === 0;
                        const isLast = index === page.likedChats?.length - 1;
                        return (
                            <LikesChatListItem 
                                key={chat.id}
                                id={chat.id}
                                slug={chat.slug}
                                myNickname={nickname!}
                                profileId={chat.profile.id}
                                profileImageUrl={chat.profile.avatar}
                                userType={chat.profile.user.type}
                                authorType={chat.authorType}
                                nickname={chat.profile.nickname}
                                content={chat.content}
                                imageUrls={chat.imageUrls}
                                isMyTypeOnly={chat.isMyTypeOnly}
                                likeCount={chat.likeCount}
                                commentCount={chat.commentCount}
                                saveCount={chat.saveCount}
                                isMine={chat.isMine}
                                isLiked={chat.isLiked}
                                isSaved={chat.isSaved}
                                isReported={chat.isReported}
                                isBlocking={chat.isBlocking}
                                hiddenAt={chat.hiddenAt}
                                createdAt={chat.createdAt}
                                isFirst={isFirst}
                                isLast={isLast}
                            />
                        );
                })
                ))}
            </VStack>
        );
    }

    /* render */

    return (
        <ChatDeleteLoadingProvider
            onComplete={() => {
                queryClient.invalidateQueries({ queryKey: ["getMyLikes"] });
                queryClient.invalidateQueries({ queryKey: ["chats"] });
            }}
        >
        <Template>
            {myProfileData?.isMe ? 
                <VStack width={"100%"} alignItems={"center"}>
                    <Stack
                        width={{
                            base: "100%",
                            sm: "100%",
                            md: "100%",
                            lg: "80%",
                            xl: "80%",
                        }}
                        spacing={0}
                        paddingY={5}
                    >
                        <HStack
                            width={"100%"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                        >
                            <Box
                                as={"button"}
                                type={"button"}
                                onClick={() => navigate(-1)}
                            >
                                <MyIcon src={"/svg/arrow-left.svg"} alt={"뒤로 가기"} width={24} height={24} />
                            </Box>
                        </HStack>
                        <HStack
                            width={"100%"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}

                            marginTop={5}
                        >
                            <Text fontSize={24} fontWeight={700} color={colors.black}>
                                좋아요
                            </Text>
                        </HStack>

                        {renderLikedChatList()}

                        {hasNextLikedChatListPage && (
                            <Button variant={"ghost"} onClick={() => fetchNextLikedChatList()} isLoading={isFetchingNextLikedChatListPage} marginTop={5}>
                                <MyIcon src={"/svg/arrow-down.svg"} alt={"더보기"} width={20} height={20} />
                            </Button>
                        )}

                    </Stack>
                </VStack>
            : 
                <VStack 
                    width={"100%"}
                    height={"80vh"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    spacing={4}
                >
                    <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"}>
                        접근 권한이 없습니다.
                    </Text>
                </VStack>
            }
        </Template>
        </ChatDeleteLoadingProvider>
    );
}